import { getVideoTokenInfo } from "api/videoshareAPI";
import VideoPlayer from "components/blocks/video/VideoPlayer";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import * as cs from "pages/auth/SignCommonStyled";
import { Text } from "components/atoms/text/Text";
import {
  GetVideoStringResponse,
  GuestVideoInfo,
  GuestVideoResponse,
} from "api/interfaces/videoshareInterface.interface";
import { CustomError } from "api/interfaces/commonInterface.interface";
import { VERIFY_TYPE } from "pages/auth/verify2fa/resendCode/resendCode";
import VerifyTitle from "components/blocks/verify/VerifyTitle";
import { TbLockOpenOff } from "react-icons/tb";
import useStatusMutation from "hook/useStatusMutation";
import useApiError from "hook/useApiError";
import styled from "styled-components";

const videoJsOptions = {
  autoplay: false,
  controls: true,
  responsive: true,
  fluid: true,
  muted: true,
  controlBar: {
    pictureInPictureToggle: false,
  },
};

export default function GuestShare(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();

  const playerReference = React.useRef(null);
  const state = location.state;

  const [tokenInfo, setTokenInfo] = useState<GuestVideoResponse | null>(null);
  const [userId, setUserId] = useState<string>("");
  const [shareEnd, setShareEnd] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { handleErrorNotify, lang, intlString, notifyError } = useApiError();

  const playerReady = (player: any) => {
    playerReference.current = player;

    // handling video player
    // player.on("waiting", () => {
    //   console.log("Video Player is waiting");
    // });
    // player.on("dispose", () => {
    //   console.log("Video player will dispose");
    // });
  };

  // console.log(tokenInfo);

  const navigateToVerify = () => {
    if (tokenInfo) {
      navigate(`/verify/2fa`, {
        state: {
          isAuth: false,
          videoInfo: { ...tokenInfo, userId },
          verifyType: VERIFY_TYPE.EMAIL,
          loginInfo: {
            email: tokenInfo.sharedEmail,
          },
        },
      });
    }
  };

  const mutationGetToken = useStatusMutation(getVideoTokenInfo, {
    onSuccess: (data: GetVideoStringResponse) => {
      if (data.error !== 0 && !data.result) {
        setShareEnd(true);
        return;
      }
      setTokenInfo({
        ...data.result,
        canDownload: data.result.canDownload ?? false,
      });
    },
    onError: (error: CustomError) => {
      if (error.response?.data !== undefined) {
        if (
          error.response?.data.error === 11003 ||
          error.response?.data.error === 11006
        ) {
          setShareEnd(true);
          return;
        }
        if (
          error.response?.data.error === 11001 ||
          error.response?.data.error === 11002
        ) {
          handleErrorNotify(error, lang.ServerErrorCode[11001]);
          return;
        }
        notifyError(lang.label.videoshare.guest.default.error);
        return;
      }
      navigate("/");
    },
    onSettled: () => {
      setIsLoading(false);
    },
  });

  useEffect(() => {
    const videoId = searchParams.get("videoId") as string;
    const userId = searchParams.get("userId") as string;

    const isNotVerified = !(state && state.isVerified);

    if (isNotVerified) {
      if (videoId && userId) {
        setUserId(userId);
        const payload: GuestVideoInfo = {
          videoId: videoId,
          userId: userId,
        };
        mutationGetToken.mutate(payload);
      }
    } else if (state?.isVerified && state?.tokenInfo) {
      setIsLoading(false);
    }
    if (searchParams.size === 0) {
      navigate("/");
    }
  }, []);

  if (!mutationGetToken.idle || isLoading) return <></>;
  if (state?.isVerified && state?.tokenInfo) {
    return (
      <GuestShareWrapper>
        <VideoPlayer
          videoUrl={state.tokenInfo.url}
          options={videoJsOptions}
          onReady={playerReady}
          isAuth={false}
          timezone={state.tokenInfo.timeZone}
          title="Guest"
          uploadStatus="success"
          videoResult={{
            startTimeMs: state.tokenInfo?.startTimeMs || "",
            recorderName: state.tokenInfo?.recorderName || "",
          }}
          videoTimezone={state.tokenInfo?.timeZone || ""}
          {...state.tokenInfo}
        />
      </GuestShareWrapper>
    );
  }
  return (
    <cs.BG noticeHeight={0}>
      <cs.Container>
        <cs.LoginBox>
          <cs.CenterBox>
            {shareEnd || !tokenInfo?.sharedEmail ? (
              <cs.ExpiredWrapper>
                <TbLockOpenOff size={100} />
                <p className="description">
                  {intlString(
                    lang.label.videoshare.guest.verification.sendCode.expired
                  )}
                </p>
              </cs.ExpiredWrapper>
            ) : (
              <>
                <VerifyTitle title={"Video Share Authentication"} desc={""} />

                <p className="description">
                  {intlString(
                    lang.label.videoshare.guest.verification.sendCode.desc
                  )}
                </p>
                <Text fontSize={14} className="user-name">
                  {tokenInfo.sharedEmail}
                </Text>
                <br />
                <br />
                <cs.Bottom>
                  <cs.BigButton
                    onClick={navigateToVerify}
                    disabled={shareEnd}
                    cancel={shareEnd}
                    data-testid="guest-video-share-video-share-button-navigate-to-verify"
                  >
                    Send Code
                  </cs.BigButton>
                </cs.Bottom>
              </>
            )}
          </cs.CenterBox>
        </cs.LoginBox>
      </cs.Container>
    </cs.BG>
  );
}

const GuestShareWrapper = styled.div`
  height: 100vh;
  width: 100%;
`;
